import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    backgroundGray: Palette['primary'];
    border: Palette['primary'];
    tertiary: Palette['primary'];
    overlay: PaletteOptions['primary'];
  }
  interface PaletteOptions {
    backgroundGray?: PaletteOptions['primary'];
    border?: PaletteOptions['primary'];
    overlay?: PaletteOptions['primary'];
    tertiary?: PaletteOptions['primary'];
  }
  interface TypographyVariants {
    body3: React.CSSProperties;
    callout: React.CSSProperties;
    h4Secondary: React.CSSProperties;
    h1Secondary: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
    callout?: React.CSSProperties;
    h4Secondary?: React.CSSProperties;
    h1Secondary?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    callout: true;
    h4Secondary: true;
    h1Secondary: true;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsVariantOverrides {
    noteField: true;
  }
}

// Light mode palette
const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: '#3E36EF',
    light: '#F1EFFF',
    dark: '#381DD9',
    contrastText: '#fff',
  },
  secondary: {
    main: '#F1EFFF',
    light: '#F9F9FA',
    dark: '#E7E5F5',
    contrastText: '#3E36EF',
  },
  tertiary: {
    main: '#9C27B0',
    light: '#FCECFF',
    dark: '#852196',
    contrastText: '#fff',
  },
  success: {
    main: '#1A832C',
    light: '#EDFFF0',
    dark: '#156923',
    contrastText: '#fff',
  },
  error: {
    main: '#D21437',
    light: '#FFEDF0',
    dark: '#B81130',
    contrastText: '#fff',
  },
  warning: {
    main: '#C44607',
    light: '#FFF3ED',
    dark: '#AB3D06',
    contrastText: '#fff',
  },
  info: {
    dark: 'rgba(0,0,0,1.0)',
    main: 'rgba(0,0,0,0.75)',
    light: 'rgba(0,0,0,0.5)',
    contrastText: '#fff',
  },
  backgroundGray: {
    main: '#FBFBFB',
    dark: '#F9F9FA',
    light: '#FAF9F7',
    contrastText: '#000000',
  },
  border: {
    main: '#E7E7E7',
    dark: '#DCDCDC',
  },
  overlay: {
    main: 'rgba(0,0,0,0.50)',
    dark: 'rgba(0,0,0,0.85)',
  },
  background: {
    default: '#ffffff',
    paper: '#ffffff',
  },
  text: {
    primary: 'rgba(0,0,0,0.87)',
    secondary: 'rgba(0,0,0,0.6)',
  },
};

// Dark mode palette
const darkPalette = {
  mode: 'dark' as const,
  primary: {
    main: '#7B73FF',
    light: '#2a2555',
    dark: '#9B95FF',
    contrastText: '#fff',
  },
  secondary: {
    main: '#2a2555',
    light: '#1a1a2e',
    dark: '#3d3670',
    contrastText: '#B3AEFF',
  },
  tertiary: {
    main: '#CE93D8',
    light: '#2d1f30',
    dark: '#E1BEE7',
    contrastText: '#fff',
  },
  success: {
    main: '#4CAF50',
    light: '#1b2e1c',
    dark: '#81C784',
    contrastText: '#fff',
  },
  error: {
    main: '#EF5350',
    light: '#2e1b1b',
    dark: '#E57373',
    contrastText: '#fff',
  },
  warning: {
    main: '#FF9800',
    light: '#2e2415',
    dark: '#FFB74D',
    contrastText: '#fff',
  },
  info: {
    dark: 'rgba(255,255,255,1.0)',
    main: 'rgba(255,255,255,0.75)',
    light: 'rgba(255,255,255,0.5)',
    contrastText: '#1a1a2e',
  },
  backgroundGray: {
    main: '#1a1a2e',
    dark: '#0f0f1a',
    light: '#252540',
    contrastText: '#ffffff',
  },
  border: {
    main: '#3d3d5c',
    dark: '#4d4d70',
  },
  overlay: {
    main: 'rgba(0,0,0,0.70)',
    dark: 'rgba(0,0,0,0.90)',
  },
  background: {
    default: '#121212',
    paper: '#1e1e2e',
  },
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255,255,255,0.7)',
  },
};

const theme = createTheme({
  palette: lightPalette,

  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
        fullWidth: true,
        slotProps: {
          htmlInput: {
            color: 'info.light',
          },
          inputLabel: {
            shrink: true,
            sx: {
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: 'text.secondary',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderColor: `#CCCCCC`,
          backgroundColor: `#FAFAFA`,
          borderRadius: `4px`,
          width: `100%`,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          '&.MuiInputLabel-outlined': {
            transform: 'translate(14px, 14px) scale(1)',
          },
          '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#f8feff',
            border: '1.5px solid #2e7980',
          },
          '&.Mui-selected:hover': {
            backgroundColor: '#f8feff',
            border: '1.5px solid #09181a',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#3E36EF',
          fontSize: '0.875rem',
          fontWeight: 600,
          fontFamily: 'area-normal, sans-serif;',
          lineHeight: 1.7,
          '&:hover': {
            color: '#09181A',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: ``,
    },
  },

  typography: {
    // Define typography settings
    h1: {
      fontSize: '2.5rem',
      lineHeight: 1.25,
      fontWeight: 500,
      fontFamily: 'p22-mackinac-pro, serif;',
    },
    h2: {
      fontSize: '1.25rem',
      fontWeight: 700,
      fontFamily: 'area-normal, sans-serif',
      lineHeight: 1.6,
    },
    h3: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 700,
      fontFamily: 'area-normal, sans-serif',
    },
    h4: {
      fontSize: '14px',
      fontWeight: 600,
      fontFamily: 'area-normal, sans-serif',
      lineHeight: '24px',
      letterSpacing: '0%',
      color: 'rgba(0, 0, 0, 0.75)',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 700,
      fontFamily: 'area-normal, sans-serif;',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      fontFamily: 'area-normal, sans-serif;',
      lineHeight: 1.7,
    },
    body3: {
      fontSize: '0.75rem',
      fontWeight: 700,
      fontFamily: 'area-normal, sans-serif;',
      lineHeight: 1.83,
    },
    callout: {
      fontSize: '0.625rem',
      fontWeight: 700,
      fontFamily: 'area-normal, sans-serif;',
      lineHeight: 1.83,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#808080',
      fontFamily: 'area-normal, sans-serif;',
      lineHeight: 1.42,
    },
    h4Secondary: {
      fontSize: '14px',
      fontWeight: 600,
      fontFamily: 'area-normal, sans-serif',
      lineHeight: '24px',
      letterSpacing: '0%',
      color: 'rgba(0, 0, 0, 0.75)',
    },
    h1Secondary: {
      fontSize: '36px',
      fontWeight: 600,
      fontFamily: 'area-normal, sans-serif',
      lineHeight: '44px',
      letterSpacing: '0%',
    },
  },
  spacing: [0, 4, 8, 16, 24, 36, 48],
});

// Function to get theme based on mode
export const getTheme = (mode: 'light' | 'dark') => {
  return createTheme({
    ...theme,
    palette: mode === 'dark' ? darkPalette : lightPalette,
  });
};

export default theme;
